import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase, getAccessToken } from "../lib/supabaseClient";
import { SBLogo } from "../components/WebLayout";
import Seo from "../components/Seo";
import {
  Plus, Loader2, LogOut, MapPin, MailWarning, Wallet,
  Building2, ChevronRight,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

async function af(path, opts = {}) {
  const t = await getAccessToken();
  return fetch(`${API}${path}`, { ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${t || ""}` } });
}

const STATUS_META = {
  draft:               { label: "Draft",              tone: "gray"  },
  submitted:           { label: "Submitted",          tone: "blue"  },
  under_review:        { label: "Under Review",       tone: "amber" },
  more_info_requested: { label: "Info Needed",        tone: "amber" },
  manager_interested:  { label: "Manager Interested", tone: "gold"  },
  agreement_pending:   { label: "Agreement Pending",  tone: "amber" },
  agreement_signed:    { label: "Agreement Signed",   tone: "green" },
  verified:            { label: "Verified",           tone: "green" },
  approved:            { label: "Verified",           tone: "green" }, // legacy display map
  marketplace_ready:   { label: "Marketplace Ready",  tone: "green" },
  sold:                { label: "Sold",               tone: "green" },
  declined:            { label: "Declined",           tone: "red"   },
  rejected:            { label: "Rejected",           tone: "red"   },
  on_hold:             { label: "On Hold",            tone: "gray"  },
  decision_keep:       { label: "Kept",               tone: "gold"  },
  decision_operator:   { label: "For Operator",       tone: "gold"  },
  decision_hold:       { label: "On Hold",            tone: "gray"  },
};

const FILTERS = [
  { key: "all",                label: "All"                },
  { key: "draft",              label: "Draft"              },
  { key: "submitted",          label: "Submitted"          },
  { key: "under_review",       label: "Under Review"       },
  { key: "manager_interested", label: "Manager Interested" },
  { key: "verified",           label: "Verified"           },
  { key: "declined",           label: "Declined"           },
  { key: "rejected",           label: "Rejected"           },
];

export default function ScoutDashboard() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [bountyTotals, setBountyTotals] = useState({ qualified: 0, earned: 0, paid: 0 });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");

  const load = useCallback(async () => {
    // If a password recovery is active, force the reset screen instead of loading dashboard data.
    try {
      if (sessionStorage.getItem("spotbounty_password_recovery_active") === "true") {
        window.location.replace("/auth/reset-password"); return;
      }
    } catch { /* ignore */ }
    setLoading(true);
    const { data: sess } = await supabase.auth.getSession();
    const u = sess?.session?.user;
    if (u) {
      setUserEmail(u.email || "");
      const md = u.user_metadata || {};
      setFirstName(md.first_name || "");
      if (!u.email_confirmed_at && !u.confirmed_at) setNeedsVerify(true);
    }
    const r = await af("/api/scouts/locations");
    if (r.status === 401 || r.status === 403) { nav("/scouts/login"); return; }
    const d = await r.json().catch(() => ({ rows: [] }));
    setRows(d.rows || []);
    setLoading(false);
  }, [nav]);
  useEffect(() => { load(); }, [load]);

  // Compute per-status counts from real rows
  const counts = useMemo(() => {
    const c = { all: rows.length, draft: 0, submitted: 0, under_review: 0,
                more_info_requested: 0, verified: 0, rejected: 0,
                decision_keep: 0, decision_operator: 0, decision_hold: 0 };
    for (const r of rows) if (r.status in c) c[r.status]++;
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    if (activeFilter === "all") return rows;
    return rows.filter((r) => r.status === activeFilter);
  }, [rows, activeFilter]);

  async function createNew() {
    setCreating(true);
    const r = await af("/api/scouts/locations", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) });
    const d = await r.json().catch(() => ({}));
    setCreating(false);
    if (r.status === 403 && d.detail === "email_not_verified") { setNeedsVerify(true); return; }
    if (d.id) nav(`/scouts/find-location/${d.id}`);
  }
  async function resendVerify() {
    if (!userEmail) return;
    setVerifyMsg("");
    const { error } = await supabase.auth.resend({ type: "signup", email: userEmail });
    setVerifyMsg(error ? error.message : "Verification email re-sent. Check your inbox.");
  }
  async function logout() { await supabase.auth.signOut(); nav("/scouts/login"); }

  const statCards = [
    { key: "all",          label: "Total Locations", tone: "text-white" },
    { key: "draft",        label: "Draft",           tone: "text-[#A1A1AA]" },
    { key: "submitted",    label: "Submitted",       tone: "text-[#3B82F6]" },
    { key: "under_review", label: "Under Review",    tone: "text-[#F59E0B]" },
    { key: "verified",     label: "Verified",        tone: "text-[#22C55E]" },
    { key: "rejected",     label: "Rejected",        tone: "text-[#EF4444]" },
  ];

  return (
    <div className="min-h-screen bg-[#080B0D] text-white">
      <Seo title="Scout Dashboard" description="Your SpotBounty locations." path="/scouts/dashboard" noindex />
      <header className="border-b border-[#2A2F34] sticky top-0 z-10 bg-[#080B0D]/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/"><SBLogo size={18} /></Link>
          <div className="flex items-center gap-3">
            <div className="text-[11.5px] text-[#A1A1AA] hidden sm:block truncate max-w-[220px]">{userEmail}</div>
            <button onClick={logout} className="text-[12px] text-[#A1A1AA] hover:text-white flex items-center gap-1" data-testid="scout-logout"><LogOut size={12} /> Sign out</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {needsVerify && (
          <div className="sb-card p-4 border-[#F59E0B]/50" style={{ background: "rgba(245,158,11,.06)" }} data-testid="scout-verify-banner">
            <div className="flex items-start gap-3">
              <MailWarning size={18} className="text-[#F59E0B] shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-[13px] font-bold">Verify your email to submit locations</div>
                <div className="text-[12px] text-[#A1A1AA] mt-1">Check your inbox at <b className="text-white">{userEmail || "your email"}</b> and click the SpotBounty confirmation link. You can browse but cannot create or submit a location until verified.</div>
                {verifyMsg && <div className="text-[11.5px] text-[#22C55E] mt-2">{verifyMsg}</div>}
                <button onClick={resendVerify} className="mt-2 text-[11.5px] font-bold text-[#FFC107] hover:underline" data-testid="scout-verify-resend">Resend verification email</button>
              </div>
            </div>
          </div>
        )}

        {/* Header row */}
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="sb-eyebrow">Scout Portal</div>
            <h1 className="mt-1 sb-headline text-[36px] md:text-[44px]">MY <span className="text-[#FFC107]">LOCATIONS.</span></h1>
            <div className="text-[13px] text-[#A1A1AA] mt-1">
              {firstName ? <>Welcome back, <b className="text-white">{firstName}</b>. </> : null}
              Track and manage your submitted vending opportunities.
            </div>
          </div>
          <button onClick={createNew} disabled={creating} className="sb-btn-primary text-[12px]" style={{ padding: "13px 18px" }} data-testid="scout-new-location">
            {creating ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Find New Location
          </button>
        </div>

        {/* Stat cards */}
        <section className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3" data-testid="scout-stats">
          {statCards.map((s) => (
            <button key={s.key} onClick={() => setActiveFilter(s.key)}
                    className={`sb-card p-4 text-left transition-colors ${activeFilter === s.key ? "border-[#FFC107]" : ""}`}
                    data-testid={`scout-stat-${s.key}`}>
              <div className="sb-eyebrow" style={{ fontSize: 9 }}>{s.label}</div>
              <div className={`mt-2 text-[20px] font-black ${s.tone}`}>{counts[s.key] ?? 0}</div>
            </button>
          ))}
        </section>

        {/* Filter tabs (mobile-friendly pills) */}
        <div className="flex gap-2 overflow-x-auto sb-scroll pb-2 -mb-2" data-testid="scout-filter-tabs">
          {FILTERS.map((f) => {
            const active = activeFilter === f.key;
            return (
              <button key={f.key} onClick={() => setActiveFilter(f.key)}
                      className={`shrink-0 px-3 py-1.5 rounded-full border text-[11.5px] font-bold uppercase tracking-widest transition-colors ${active ? "border-[#FFC107] text-[#FFC107] bg-[#FFC107]/5" : "border-[#2A2F34] text-[#A1A1AA] hover:text-white"}`}
                      data-testid={`scout-filter-${f.key}`}>
                {f.label} <span className="ml-1 opacity-70">{counts[f.key] ?? 0}</span>
              </button>
            );
          })}
        </div>

        {/* Locations list */}
        <section data-testid="scout-locations-list">
          {loading && (
            <div className="sb-card p-8 text-[#A1A1AA] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="sb-card p-10 text-center">
              <div className="mx-auto w-12 h-12 rounded-lg bg-[#FFC107]/10 text-[#FFC107] flex items-center justify-center"><MapPin size={20} /></div>
              <div className="mt-3 font-black">{activeFilter === "all" ? "No locations yet" : "Nothing in this filter"}</div>
              <div className="mt-1 text-[13px] text-[#A1A1AA]">Tap &ldquo;Find New Location&rdquo; to submit your first vending opportunity.</div>
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <div className="grid gap-3">
              {filtered.map((r) => {
                const m = STATUS_META[r.status] || { label: r.status, tone: "gray" };
                const canEdit = r.status === "draft" || r.status === "more_info_requested";
                return (
                  <div key={r.id} onClick={() => nav(`/scouts/find-location/${r.id}`)}
                       className="sb-card p-4 cursor-pointer hover:border-[#FFC107]/40 transition-colors flex items-center gap-4"
                       data-testid={`scout-loc-${r.id}`}>
                    <div className="w-10 h-10 rounded-lg bg-[#FFC107]/10 border border-[#2A2F34] flex items-center justify-center shrink-0">
                      <Building2 size={16} className="text-[#FFC107]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[14px] font-bold truncate">{r.business_name || "Untitled Location"}</div>
                      <div className="text-[11.5px] text-[#A1A1AA] mt-0.5 truncate">
                        {r.city ? `${r.city}${r.state ? `, ${r.state}` : ""}` : "no address yet"} · Created {new Date(r.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <span className={`sb-pill sb-pill-${m.tone}`}>{m.label}</span>
                    <div className="text-[11px] text-[#A1A1AA] font-bold uppercase tracking-widest hidden sm:block">
                      {canEdit ? "Continue" : "View"}
                    </div>
                    <ChevronRight size={14} className="text-[#52525b]" />
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
